import './App.css'
import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import LazyImage from './LazyImage'

function AIBlog2(): JSX.Element {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="page blog-page">
      <nav className="page-nav">
        <Link to="/" className="btn btn-secondary">← Back to Home</Link>
        <Link to="/blog/ai" className="btn btn-secondary">← AI Blog Part I</Link>
      </nav>

      <section className="page-header">
        <h1>Artificial Intelligence (AI) Part II: Nonlinear Classification</h1>
        <p className="page-subtitle">Understanding nonlinear neural classifiers and why AI remains pattern recognition</p>
        <div className="blog-meta">
          <span>📚 Category: AI Research</span>
          <span>✍️ Author: Dr Qing Song</span>
          <span>🔬 Part II: Nonlinear Models</span>
        </div>
      </section>

      <div className="blog-container">
        <article className="blog-article">
          <section className="blog-section">
            <h2>Introduction</h2>
            <div className="intro-highlight">
              <p>
                This blog series explores the fundamentals of artificial intelligence, focusing on
                neural networks and their applications in nonlinear classification problems.
              </p>
              <p>
                While ChatGPT and other large language models have brought AI into the spotlight,
                they are still pattern recognition systems trained on data. They do not possess true
                human consciousness or reasoning ability.
              </p>
            </div>
          </section>

          <section className="blog-section">
            <h2>AI Blog Series 2: What can neural networks do with data as a nonlinear classifier?</h2>
            <div className="section-content">
              <p>
                A nonlinear classifier with two-dimensional (2D) input vectors can be used to classify
                patterns into two classes. To do this, the network needs at least three activation
                functions (three TLUs) as shown in Figure 1 below with more weight parameters than that in the linear classifier of my blog AI part I.
              </p>

              <ul className="ai-applications-list">
                <li>🔁 Solve nonlinear classification problems such as XOR</li>
                <li>🧠 Use multiple decision boundaries instead of a single straight line</li>
                <li>📊 Represent richer pattern relationships with extra neurons</li>
              </ul>

              <figure className="blog-figure">
                <div className="image-container">
                  <LazyImage
                    src="https://cdn.eqsong.com/PDF/Figure-1.jpg"
                    alt="Nonlinear classifier with three neurons and threshold logic units"
                    className="blog-image"
                  />
                </div>
                <figcaption><strong>Figure 1:</strong> A nonlinear classifier with three neurons (TLUs)</figcaption>
              </figure>

              <figure className="blog-figure">
                <div className="image-container">
                  <LazyImage
                    src="https://cdn.eqsong.com/PDF/Figure-2.png"
                    alt="Nonlinear XOR classification problem with circles and squares"
                    className="blog-image"
                  />
                </div>
                <figcaption><strong>Figure 2:</strong> A nonlinear (XOR) classification problem</figcaption>
              </figure>

              <p>
                In this nonlinear case as shown in Figure 2, there are four patterns: two squares for class 1 and two circles for class 2.
                A single straight line cannot separate them. The nonlinear classifier instead uses a
                combination of multiple boundaries to distinguish the classes, i.e. the two straight lines in Figure 2. Two circles of the area between the two straight lines are separated from two squares outside the area of the two straight lines. 
                This forms a nonlinear classification problem.
              </p>

              <div className="key-insight">
                <h3>🎯 The Critical Transformation: Pattern Space to Image Space</h3>
                <p>
                  The third neuron in the second layer (Figure 1) generates the nonlinear classifier output.
                  The <strong>critical point</strong> is that the first layer of two neurons (TLUs) in Figure 1
                  maps the input patterns A, B, C, and D from Figure 2 to a <em>new image space</em> in Figure 3. 
                  This transformation is crucial for enabling linear separation in the new image space.
                </p>
                <p>
                  <strong>Key transformation:</strong> In this new image space, the horizontal and vertical axes represent
                  the outputs of the first layer's two neurons (TLUs). The original patterns A, B, C, and D in Figure 2
                  are mapped to new positions in Figure 3. Remarkably, patterns A and D are mapped
                  into a <strong>single point</strong>, while patterns B and C are positioned in another part of the image space.
                </p>
              </div>

              <figure className="blog-figure">
                <div className="image-container">
                  <LazyImage
                    src="https://cdn.eqsong.com/PDF/Figure3-2.jpg"
                    alt="Nonlinear classification problem transformed into the image space"
                    className="blog-image"
                  />
                </div>
                <figcaption><strong>Figure 3:</strong> The nonlinear classification problem in the image space</figcaption>
              </figure>

              <div className="key-insight">
                <p>
                  <strong>This means patterns can be linearly separated in this transformed image space!</strong>
                  Therefore, a single straight line (hyperplane) generated by the third neuron (TLU) in Figure 1
                  can separate all the squares from the circles in Figure 3.
                </p>
                <p>
                  <strong>💡 This is the most important basic principle of a nonlinear classifier using neural networks.</strong>
                  This is also the fundamental concept behind more advanced AI systems based on deep learning neural networks like ChatGPT today.
                </p>
              </div>

              <div className="warning-box">
                <h3>Important reminder</h3>
                <p>
                  <strong>AI models like ChatGPT do not possess consciousness. Their apparent reasoning is still
                  a form of classification and generalization based on training data with a multilayered (much more than the two layers shown in Figure 1) nonlinear classifier and a huge number of neurons and parameters .</strong>
                </p>
                <p>
                  <strong>The intelligence that we see in AI models like ChatGPT is pattern recognition or regression, not true human understanding.</strong>
                </p>
              </div>

              <p>
                Neural network based AI remains powerful for processing massive data with complex patterns,
                enabling results that are impossible for human brains to compute quickly.
              </p>
            </div>
          </section>

          <section className="blog-section" style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '8px', marginTop: '40px' }}>
            <h2>Refereed Academic Journal Publications</h2>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>Sumit Bam Shrestha* and Qing Song##, "Robustness to Training Disturbances in SpikeProp Learning",
            <em> IEEE Transactions on Neural Networks and Learning Systems</em>, pp.3126-3139, Vol. 29, No.7, July, 2018.</li>
          <li>Sumit Bam Shrestha* and Qing Song##, "Robust spike-train learning in spike-event based weight update", 
            <em> Neural Networks</em>, Volume 96, December 2017, Pages 33-46.</li>
          <li>Qing Song##, X. Zhao*, H. J. Fan* and D.W. Wang, "Robust Recurrent Kernel Online Learning",
            <em> IEEE Transactions on Neural Networks and Learning Systems</em>, pp. 1068-1081, Vol. 28, No. 5, May, 2017.</li>
          <li>Sumit Bam Shrestha*, Qing Song##, "Robust learning in SpikeProp", <em> Neural Networks</em>, Volume 86, February 2017, Pages 54-68.</li>
          <li>Xulei Yang*, Qing Song and Yi Su, "Automatic segmentation of left ventricle cavity from short-axis cardiac magnetic resonance images", 
            <em> Medical & Biological Engineering & Computing</em>, Feb. 2017.</li>
          <li>Sumit Bam Shrestha*, Qing Song##, "Adaptive learning rate of SpikeProp based on weight convergence analysis", 
            <em> Neural Networks</em>, 03/2015.</li>
          <li>Haijin Fan*, Qing Song, Zhao Xu*, "An information theoretic sparse kernel algorithm for online learning", 
            <em> Expert Systems with Applications</em> 07/2014; 41(9):4349–4359.</li>
          <li>Haijin Fan*, Qing Song#, Sumit Bam Shrestha*, "Online Learning with Kernel Regularized Least Mean Square Algorithms", 
            <em> Knowledge-Based Systems</em> 03/2014.</li>
          <li>Haijin Fan*, Qing Song#, "A linear recurrent kernel online learning algorithm with sparse updates", 
            <em> Neural networks</em>, 11/2013.</li>
          <li>Zhimin Wang*, Qing Song#, Y.C. Soh, Sim Kang, "An adaptive spatial information-theoretic fuzzy clustering algorithm for image segmentation", 
            <em> Computer Vision and Image Understanding</em> 10/2013; 117(10):1412-1420.</li>
          <li>Qing Song#, <a href="https://bird-created.s3.us-east-1.amazonaws.com/PDF%2FRobust-Initialization-of-a-Jordan-Network-With-Recurrent-Constrained-Learning.pdf" target="_blank" rel="noopener noreferrer" style={{color: '#0066cc', textDecoration: 'none'}}>"Robust initialization of a Jordan Network with Recurrent Constrained Learning"</a>,
            Special issue of White Box Nonlinear Prediction Models, <em> IEEE Transactions on Neural Networks</em>,
            Vol. 22, No. 12, Part II, pp. 2460-2473, 2011.</li>
          <li>Z. Xu*, Qing Song and D.Wang, "Recurrent neural tracking control based on multivariable robust adaptive gradient-descent training algorithm", 
            <em> Neural Computing & Applications</em>, No. 11, Vol. 20, 2011.</li>
          <li>Qing Song#, "On the Weight Convergence of Elman Networks", <em> IEEE Transactions on Neural Networks</em>, Vol. 21, No.3, pp.463-480, 2010.</li>
          <li>Z.M. Wang*, Qing Song#, Y.C. Soh, and Sim Kang, "A Robust Curve Clustering based on a Multivariate Model", 
            <em> IEEE Transactions on Neural Networks</em>, Vol. 21, No. 12, 2010, pp.1976-1984.</li>
          <li>Qing Song#, X.L. Yang* and Y.C. Soh, "An Information Fuzzy C-Spherical Shells Clustering Algorithm", 
            <em> Fuzzy Sets and Systems</em>, Vol. 161, No. 13, pp.1755-1773, 2010.</li>
          <li>Y.L. Wu*, Sun, FC, Zheng, JC and Qing Song, "A robust training algorithm of discrete-time MIMO RNN and application in fault tolerant control of robotic system", 
            <em> Neural Computing & Applications</em> Vol. 19, No. 7, pp. 1013-1027, 2010.</li>
          <li>X. L. Yang*, Qing Song, Y.L. Wu* "A Novel Pruning Approach for Robust Data Clustering", 
            <em> Neural Computing & Applications</em>, Vol. 18, No.7, pp.759-768, 2009.</li>
          <li>Z.M. Wang, Y.C. Soh, Qing Song# and Sim Kang, "Adaptive Spatial Information-theoretic Clustering for Image Segmentation", 
            <em> Pattern Recognition</em>, Vol. 42., 2009, pp. 2029-2044.</li>
          <li>C.Y. Guo* and Qing Song#, "Real-time control of variable air volume system based on a robust neural network associated PI controller", 
            <em> IEEE Transactions on Control Systems Technology</em>, Vol.17, No. 3, 2009, pp.600-607.</li>
          <li>Qing Song#, Y.L. Wu* and Y.C. Soh "Robust Adaptive Gradient Descent Training Algorithm for Recurrent Neural Networks in Discrete Time Domain", 
            <em> IEEE Transactions on Neural Networks</em>, Vol.19, No. 11, 2008, pp.1841-1853.</li>
          <li>Qing Song#, J. Spall, Y.C. Soh and J. Ni*, "Robust Neural Network Tracking Controller Using Simultaneous Perturbation Stochastic Approximation", 
            <em> IEEE Transactions on Neural Networks</em>, Vol.19, No.5, 2008, pp. 817-835.</li>
          <li>Y.L. Wu*, Qing Song#, and S. Liu, "A Normalized Adaptive Training of Recurrent Neural Networks with Augmented Error Gradient", 
            <em> IEEE Transactions on Neural Networks</em>, Vol.19, No.2, 2008, pp.351-356.</li>
          <li>A. Z. Cao* and Qing Song#, "Robust Information Clustering for Automatic Breast Mass Detection in Digitized Mammograms", 
            <em> Computer Vision and Image Understanding</em>, No. 1, Vol. 109, 2008, pp.86-96.</li>
          <li>X. L. Yang*#, Qing Song and A. Z. Cao*, "A Modified Deterministic Annealing Algorithm for Robust Image Segmentation", 
            <em> Journal of mathematical imaging and vision</em>, Vol. 30, No. 3, 2008, pp. 308-324.</li>
          <li>X.L. Yang*, Qing Song, "A weighted support vector machine for data classification",
            <em> International Journal of Pattern Recognition and Artificial Intelligence</em>, No. 5, Vol. 21, 2007, pp.961-976.</li>
          <li>J. Ni* and Qing Song, "Pruning Based Robust Backpropagation Training Algorithm for RBF Network Tracking Controller", 
            <em> Journal of Intelligent and Robotics Systems</em>, Vol. 48, No.3, 2007, pp.375-396.</li>
          <li>Y.L. Wu* and Qing Song, "Robust Recurrent Neural Control of Biped Robot",
            <em> Journal of Intelligent and Robotics Systems</em>, No. 2, Vol. 49, 2007, pp.151-169.</li>
          <li>X.L. Yang* and Qing Song#, "A Robust Deterministic Annealing Algorithm for Data Clustering",
            <em> Data & Knowledge Engineering</em>, 62 (1), 2007, pp.84-100.</li>
          <li>C.Y. Guo*, Qing Song#, and W.J. Cai, "Neural Network Assisted Cascade Control System for Air Handling Unit", 
            <em> IEEE Transactions On Industrial Electronic</em>, Vol. 54, No.1, 2007, pp.620-628.</li>
          <li>J. Ni* and Qing Song. "Dynamic Pruning Algorithm for Multilayer Perceptron Based Neural Control Systems", 
            <em> Journal of Neurocomputing</em>, Vol. 69, Issues 16-18, 2006.</li>
          <li>X.L. Yang* and Qing Song, Kernel-based deterministic annealing algorithm for data clustering,
            <em> IEE Proceedings: Vision, Image and Signal Processing</em> 153 (5), pp. 557-568, 2006.</li>
          <li>X.L. Yang*, Qing Song, A.Z. Cao*, "A weighted deterministic annealing algorithm for data clustering", 
            <em> International Journal of Computational Intelligent Research</em>, Vol.2, No.1, Pages:81-85, 2006.</li>
          <li>X.L. Yang*, Qing Song# and A. Z. Cao*, "A New Cluster Validity for Data Clustering", <em> Neural Processing Letter</em>, 23 (3), 2006, pp. 325-344.</li>
          <li>Qing Song#, <a href="https://bird-created.s3.us-east-1.amazonaws.com/PDF%2FA-Robust-Information-Clustering-Algorithm.pdf" target="_blank" rel="noopener noreferrer" style={{color: '#0066cc', textDecoration: 'none'}}>"A Robust Information Clustering Algorithm"</a>, <em> Neural Computation</em>, MIT, Vol.17, No.12, 2005, pp.2672-2698.</li>
          <li>W.J.Hu* and Qing.Song "An Accelerated Training Algorithm for Robust Support Vector Machine",
            <em> IEEE Transactions on Circuits and Systems II</em>, Vol. 51, No.5, 2004.</li>
          <li>Qing. Song#, W. J. Hu*, "Robust Neural Controller for VAV System", <em> IEE Proceedings Part D - Control Theory and Applications</em>, Vol. 150, No. 2, 2003.</li>
          <li>Qing Song, W. J. Hu*, W. F. Xie**, <a href="https://bird-created.s3.us-east-1.amazonaws.com/PDF%2FRobust_support_vector_machine_with_bulle.pdf" target="_blank" rel="noopener noreferrer" style={{color: '#0066cc', textDecoration: 'none'}}>"Robust Support Vector Machine for Bullet Hole Image Classification"</a>, 
            <em> IEEE Transactions on Systems, Man and Cybernetics, Part C</em>, Vol. 32, 2002.</li>
          <li>Qing Song, L. Yin, Y. C. Soh, "Robust Identification of Nonlinear Plant using Neural Networks",
            <em> Asian Journal of Control</em>, Vol. 3, No. 2, 2001, Special Issue on Advances in Neural and Fuzzy Controllers.</li>
          <li>Qing Song, L. Yin, Y. C. Soh, "Robust Adaptive Dead Zone Technology for Fault Diagnosis and Control of Robot Manipulators using Neural Networks", 
            <em> Journal of Intelligent and Robotics Systems</em>, the Netherlands, 2001.</li>
          <li>Qing Song, Lin Yin, "Robust Adaptive Fault Accommodation for Robot System using RBF Neural Network", 
            <em> International Journal of Systems Science</em>, UK, Vol. 32, No. 2, 2001.</li>
          <li>Qing Song, "Design of Robust Neural Tracking Controller", <em> Journal of Intelligent and Robotics Systems</em>, the Netherlands, Vol. 20, 2000.</li>
          <li>D. J. Hou, Qing Song, "Computerised Auto-Scoring System Based upon Feature Extraction and Neural Network Technologies", 
            <em> Journal of Intelligent and Robotics Systems</em>, the Netherlands, Vol. 29, 2000, Special Issue of Neural Network and Image Processing.</li>
          <li>Qing Song#, J. Xiao, Y. C. Soh, "Robust Back-Propagation Algorithm for Multi-Layered Neural Tracking Controller", 
            <em> IEEE Transactions on Neural Networks</em>, USA, Vol. 10, No. 5, 1999, pp. 1133-1141.</li>
          <li>Qing Song, "Implementation of Two Dimensional Systolic Algorithms for Multilayered Neural Networks", 
            <em> Journal of Systems Architecture</em>, Italy, Vol. 48, pp.1209-1218, 1999.</li>
          <li>Qing Song, L. Yin, Y. C. Soh, "Shifting of the Center of Radial Basis Function Neural Network in the Presence of Disturbance", 
            <em> Neural and Parallel Computation</em>, USA, Vol. 7, No. 1, pp. 121-131, 1999.</li>
          <li>Qing Song, "Robust Training Algorithm of Multi-Layered Neural Network for Identification of Nonlinear Dynamic Systems", 
            <em> IEE Proceedings Part D - Control Theory and Applications</em>, UK, Vol. 145, No. 1, 1998.</li>
          <li>Qing Song, M. J. Grimble, "Design of a Multivariable Neural Controller and its Application to Gas Turbine", 
            <em> ASME Journal of Dynamic Systems, Measurement and Control</em>, USA, Vol. 119, No. 3, 1997.</li>
          <li>Qing Song, J. Xiao, "On the Convergence Performance of Multi-Layered NN Tracking Controller",
            <em> Neural and Parallel Computation</em>, USA, Vol. 5, No. 3, 1997.</li>
          <li>Qing Song, "Training of a Single Perceptron Neuron using Robust Projection Algorithm", <em> Neural and Parallel Computation</em>, USA, Vol. 4, No. 2, pp. 193-203, 1996.</li>
          <li>Qing Song, E. K. Teoh, D. P. Mital, "Multilayered Neural Network Implementation on Transputer Systolic Array", 
            <em> Journal of Microprocessing and Microprogramming</em>, the Netherlands, Vol. 41, , pp. 289-299, 1995.</li>
          <li>Qing Song, M. R. Katebi, M. J. Grimble, "Robust Multivariable Implicit Adaptive Control", <em> IMA Journal of Mathematical Control and Information</em>, UK, Vol. 10, pp. 49-70, 1993.</li>
          <li>Qing Song, J. Wilkie, M. J. Grimble, "Robust Controller for Gas Turbines Based Upon LQG/LTR Design with Self-Tuning Features", 
            <em> ASME Journal of Dynamic Systems, Measurement and Control</em>, USA, Vol. 115, No. 3, pp. 569-571, 1993.</li>
        </ol>
      </section>

          <section className="blog-section">
            <div className="resources-grid">
              <Link to="/" className="resource-link" aria-label="Return to homepage">
                <span className="resource-icon">🏠</span>
                <span className="resource-text">Back to Home</span>
              </Link>
              <Link to="/blog/ai" className="resource-link" aria-label="Browse AI Blog Part I">
                <span className="resource-icon">📚</span>
                <span className="resource-text">AI Blog Part I</span>
              </Link>
            </div>
          </section>
        </article>
      </div>
    </div>
  )
}

export default AIBlog2


























































































































































































































































































































































































































































































































